import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import { AuthContext } from "../../context/AuthContext";
import ENDPOINT from "../../data/ENDPOINT";
import SetPage from "../../data/SetPage";

/* pdf.js (UMD build, loaded from CDN so the old CRA toolchain is untouched) */
const PDFJS_SRC =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js";
const PDFJS_WORKER =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

const loadPdfJs = () =>
  new Promise((resolve, reject) => {
    if (window.pdfjsLib) return resolve(window.pdfjsLib);
    const existing = document.getElementById("pdfjs-lib");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.pdfjsLib));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = "pdfjs-lib";
    s.src = PDFJS_SRC;
    s.onload = () => resolve(window.pdfjsLib);
    s.onerror = reject;
    document.body.appendChild(s);
  });

/* Renders a PDF into <canvas> pages. The browser never gets the PDF file
 * itself, so there is no viewer toolbar, no download button and nothing for
 * "Save as" to grab — the context menu is suppressed as well. */
const PdfCanvasViewer = ({ url }) => {
  const hostRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      setStatus("loading");
      const host = hostRef.current;
      if (host) host.innerHTML = "";
      try {
        const pdfjsLib = await loadPdfJs();
        if (!pdfjsLib) throw new Error("pdf.js unavailable");
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;

        /* X-Note-View proves this is an in-app read: a pasted url opened in a
         * tab is a navigation and cannot set custom headers, so the server
         * refuses it and the file can't be rendered/saved that way. */
        const res = await axios.get(url, {
          responseType: "arraybuffer",
          headers: { "X-Note-View": "1" },
        });
        if (cancelled) return;

        const pdf = await pdfjsLib.getDocument({ data: res.data }).promise;
        if (cancelled) return;

        const boxWidth = (host && host.clientWidth) || 800;
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          if (cancelled) return;

          const base = page.getViewport({ scale: 1 });
          const scale = (boxWidth / base.width) * 1.5; // crisp on retina
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          canvas.style.display = "block";
          canvas.style.marginBottom = "12px";
          canvas.style.borderRadius = "8px";
          canvas.style.boxShadow = "0 2px 10px rgba(17,24,39,.10)";
          if (cancelled) return;
          host.appendChild(canvas);

          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          }).promise;
        }
        if (!cancelled) setStatus("ready");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };

    if (url) render();
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div
      className="bn-protected"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <div ref={hostRef} />
      {status === "loading" ? (
        <p className="text-muted mb-0">Loading document...</p>
      ) : null}
      {status === "error" ? (
        <p className="text-danger mb-0">
          Could not display this note. Please refresh and try again.
        </p>
      ) : null}
    </div>
  );
};

/* Student view of the PDF / PPT notes an admin attached to their batch.
 * Route: /batch-notes/:course_url/:batch_id */
const BatchNotes = () => {
  const { course_url, batch_id } = useParams();
  const { user } = useContext(AuthContext);

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    SetPage("Notes");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!user || !user.SessionId) return;
      setLoading(true);
      try {
        const data = new FormData();
        data.append("session", user.SessionId);
        data.append("course_url", course_url);
        data.append("batch_id", batch_id);
        const res = await axios.post(ENDPOINT + "get-batch-notes.php", data, {
          headers: { "content-type": "multipart/form-data" },
        });
        const list =
          res && res.data && res.data.status === "success"
            ? res.data.data || []
            : [];
        setNotes(list);
        setSelected(list.length ? list[0] : null);
      } catch (e) {
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [course_url, batch_id, user]);

  /* PDFs are drawn to canvas (no file reaches the browser). PPT still needs the
   * Microsoft viewer because browsers cannot render .pptx natively. */
  const officeSrc = useMemo(
    () =>
      selected && selected.file_type !== "pdf"
        ? selected.office_url || null
        : null,
    [selected],
  );

  const prettySize = (kb) =>
    !kb ? "" : kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;

  return (
    <div className="batch-notes-page">
      <Helmet>
        <title>Batch Notes - SiliconMinds</title>
      </Helmet>

      <style>{`
        .batch-notes-page {
          background: #f5f6f8;
          min-height: 100vh;
          padding: 24px 0 48px;
        }
        @media (max-width: 767px) {
          .batch-notes-page { padding-top: 76px; }
        }
        .bn-card {
          background: #fff;
          border: 1px solid #e9edf2;
          border-radius: 14px;
        }
        .bn-item {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 14px; border-radius: 12px;
          cursor: pointer; border: 1px solid transparent;
          transition: all .15s ease; margin-bottom: 8px;
          background: #fff;
        }
        .bn-item:hover { background: #fafbfc; border-color: #e9edf2; }
        .bn-item.active {
          background: rgba(255,129,68,.08);
          border-color: rgba(255,129,68,.30);
        }
        /* mobile + tablet: the notes become a horizontal strip, so the viewer
           sits right below them instead of under a long vertical list */
        @media (max-width: 991px) {
          .bn-list {
            display: flex;
            gap: 10px;
            overflow-x: auto;
            overflow-y: hidden;
            padding-bottom: 6px;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x proximity;
          }
          .bn-list::-webkit-scrollbar { height: 6px; }
          .bn-list::-webkit-scrollbar-thumb {
            background: #d7dbe0; border-radius: 999px;
          }
          .bn-list .bn-item {
            flex: 0 0 auto;
            width: 210px;
            margin-bottom: 0;
            scroll-snap-align: start;
          }
        }
        .bn-type {
          width: 42px; height: 42px; border-radius: 10px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 700;
        }
        .bn-pdf { background: rgba(229,57,53,.10); color: #c62828; }
        .bn-ppt { background: rgba(245,124,0,.12); color: #e65100; }
        .bn-title { font-weight: 600; color: #16202c; margin: 0; font-size: 14.5px; }
        .bn-meta { font-size: 12px; color: #9aa1ab; margin: 2px 0 0; }
        .bn-viewer {
          width: 100%; height: 72vh; min-height: 420px;
          border: none; border-radius: 12px; background: #fff;
        }
        @media (max-width: 767px) {
          .bn-viewer { height: 60vh; min-height: 340px; }
        }
        /* canvas-rendered PDF pages — nothing here is a downloadable file */
        .bn-protected {
          height: 72vh; min-height: 420px;
          overflow-y: auto; overflow-x: hidden;
          background: #f7f8fa; border-radius: 12px; padding: 12px;
          -webkit-user-select: none; -moz-user-select: none; user-select: none;
        }
        .bn-protected canvas { pointer-events: none; }
        @media (max-width: 767px) {
          .bn-protected { height: 60vh; min-height: 340px; }
        }
      `}</style>

      <div className="container">
        {/* header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <h4 className="mb-1 fw-bold text-dark">Batch Notes</h4>
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
              Study material shared by your trainer for this batch.
            </p>
          </div>
          <Link
            to={`/course-dashboard/${course_url}/${batch_id}`}
            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
          >
            Back to course
          </Link>
        </div>

        {loading ? (
          <div className="bn-card p-5 text-center">
            <p className="text-muted mb-0">Loading notes...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="bn-card p-5 text-center">
            <h6 className="fw-bold mb-1">No notes yet</h6>
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
              Your trainer hasn't shared any PDF or PPT notes for this batch yet.
            </p>
          </div>
        ) : (
          <div className="row g-4">
            {/* list */}
            <div className="col-12 col-lg-4">
              <div className="bn-card p-3">
                <h6 className="fw-bold mb-3">
                  All notes
                </h6>
                <div className="bn-list">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className={`bn-item ${
                        selected && selected.id === note.id ? "active" : ""
                      }`}
                      onClick={() => setSelected(note)}
                    >
                      <div
                        className={`bn-type ${
                          note.file_type === "pdf" ? "bn-pdf" : "bn-ppt"
                        }`}
                      >
                        {note.file_type === "pdf" ? "PDF" : "PPT"}
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <p className="bn-title text-truncate">{note.title}</p>
                        <p className="bn-meta text-truncate">
                          {prettySize(note.size_kb)}
                          {note.uploaded_on ? ` • ${note.uploaded_on}` : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* viewer */}
            <div className="col-12 col-lg-8">
              <div className="bn-card p-3">
                {selected ? (
                  <>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                      <div className="overflow-hidden">
                        <h6 className="fw-bold mb-0 text-truncate">
                          {selected.title}
                        </h6>
                        {selected.description ? (
                          <p
                            className="text-muted mb-0 text-truncate"
                            style={{ fontSize: 13 }}
                          >
                            {selected.description}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {selected.file_type === "pdf" ? (
                      <PdfCanvasViewer
                        key={selected.id}
                        url={selected.view_url}
                      />
                    ) : (
                      <div onContextMenu={(e) => e.preventDefault()}>
                        <iframe
                          key={selected.id}
                          className="bn-viewer"
                          src={officeSrc}
                          title={selected.title}
                          allowFullScreen
                        />
                      </div>
                    )}

                    <p
                      className="text-muted mt-2 mb-0"
                      style={{ fontSize: 12 }}
                    >
                      This material is view-only and cannot be downloaded.
                    </p>
                  </>
                ) : (
                  <p className="text-muted mb-0">Select a note to read it.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchNotes;
